import type { TImageContentItem, TPageContentItem, TTextBoxContentItem } from '@src/types/wysiwyg_editor';

import type { TPositionableTextDocument } from '@src/lib/PositionableTextDocument';
import type { TRect } from '@src/types/misc';
import {
  WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_CLEAR_IMAGE,
  WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD,
  WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD,
  WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE,
  WYSIWYG_EDITOR_REDO,
  WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES,
  WYSIWYG_EDITOR_SAVE_SNAPSHOT,
  WYSIWYG_EDITOR_SET_BACKGROUND_IMAGE_ID,
  WYSIWYG_EDITOR_SET_LAYOUT_TEMPLATE_NAME,
  WYSIWYG_EDITOR_TEXT_CACHE_ADD_ITEM,
  WYSIWYG_EDITOR_TEXT_CACHE_REMOVE_ITEM,
  WYSIWYG_EDITOR_UNDO,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION,
  WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS,
  WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM,
  WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM,
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT,
  WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR
} from '@src/constants/wysiwyg_editor';

export interface AddPageContentItemAction {
  type: 'WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM';
  payload: TPageContentItem;
}

export function addPageContentItem(newItem: TPageContentItem): AddPageContentItemAction {
  return {
    type: WYSIWYG_EDITOR_ADD_PAGE_CONTENT_ITEM,
    payload: newItem
  };
}

export interface DeletePageContentItemAction {
  type: 'WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM';
  payload: string;
}

export function deletePageContentItem(id: string): DeletePageContentItemAction {
  return {
    type: WYSIWYG_EDITOR_DELETE_PAGE_CONTENT_ITEM,
    payload: id
  };
}

export interface MovePageContentItemBackwardAction {
  type: 'WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD';
  payload: string;
}

export function movePageContentItemBackward(id: string): MovePageContentItemBackwardAction {
  return {
    type: WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_BACKWARD,
    payload: id
  };
}

export interface MovePageContentItemForwardAction {
  type: 'WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD';
  payload: string;
}

export function movePageContentItemForward(id: string): MovePageContentItemForwardAction {
  return {
    type: WYSIWYG_EDITOR_MOVE_PAGE_CONTENT_ITEM_FORWARD,
    payload: id
  };
}

export interface MoveTextContentToCacheAction {
  type: 'WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE';
  payload: TTextBoxContentItem;
}

export function moveTextContentToCache(pageItem: TTextBoxContentItem): MoveTextContentToCacheAction {
  return {
    type: WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE,
    payload: pageItem
  };
}

export interface SetBackgroundImageIdAction {
  type: 'WYSIWYG_EDITOR_SET_BACKGROUND_IMAGE_ID';
  payload: number | null;
}

export function setBackgroundImageId(newValue: number | null): SetBackgroundImageIdAction {
  return {
    type: WYSIWYG_EDITOR_SET_BACKGROUND_IMAGE_ID,
    payload: newValue
  };
}

export interface SetLayoutTemplateNameAction {
  type: 'WYSIWYG_EDITOR_SET_LAYOUT_TEMPLATE_NAME';
  payload: string;
}

export function setLayoutTemplateName(newValue: string): SetLayoutTemplateNameAction {
  return {
    type: WYSIWYG_EDITOR_SET_LAYOUT_TEMPLATE_NAME,
    payload: newValue
  };
}

export interface ReplaceLayoutImageItemsAction {
  type: 'WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES';
  payload: Array<TPageContentItem>;
}

export function replaceLayoutImageItems(newItems: Array<TPageContentItem>): ReplaceLayoutImageItemsAction {
  return {
    type: WYSIWYG_EDITOR_REPLACE_PAGE_CONTENT_IMAGES,
    payload: newItems
  };
}

export interface ClearImageAction {
  type: 'WYSIWYG_EDITOR_CLEAR_IMAGE';
  payload: string;
}

export function clearImage(id: string): ClearImageAction {
  return {
    type: WYSIWYG_EDITOR_CLEAR_IMAGE,
    payload: id
  };
}

export interface UpdateContentItemAction {
  type: 'WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM';
  payload: {
    id: string;
    newData: TImageContentItem;
  };
}

export function updateContentItem(id: string, newData: TImageContentItem): UpdateContentItemAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_PAGE_CONTENT_ITEM,
    payload: { id, newData }
  };
}

export interface UpdateContentItemContentLocationAction {
  type: 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION';
  payload: {
    id: string;
    newLocation: TRect;
  };
}

export function updateContentItemContentLocation(
  id: string,
  newLocation: TRect
): UpdateContentItemContentLocationAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTENT_LOCATION,
    payload: { id, newLocation }
  };
}

export interface UpdateContentItemContainerLocationAction {
  type: 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION';
  payload: {
    id: string;
    newLocation: TRect;
  };
}

export function updateContentItemContainerLocation(
  id: string,
  newLocation: TRect
): UpdateContentItemContainerLocationAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_CONTAINER_LOCATION,
    payload: { id, newLocation }
  };
}

export interface UpdateContentItemLocationsAction {
  type: 'WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS';
  payload: {
    id: string;
    newContainerLocation: TRect;
    newContentLocation: TRect;
  };
}

export function updateContentItemLocations(
  id: string,
  newContainerLocation: TRect,
  newContentLocation: TRect
): UpdateContentItemLocationsAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_CONTENT_ITEM_LOCATIONS,
    payload: { id, newContainerLocation, newContentLocation }
  };
}

export interface UpdateTextContentItemContentAction {
  type: 'WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT';
  payload: {
    id: string;
    content: TPositionableTextDocument | null | undefined;
  };
}

export function updateTextContentItemContent(
  id: string,
  content: TPositionableTextDocument | null | undefined
): UpdateTextContentItemContentAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_CONTENT,
    payload: { id, content }
  };
}

export interface UpdateTextContentItemBackgroundColorAction {
  type: 'WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR';
  payload: {
    id: string;
    color: string | null | undefined;
  };
}

export function updateTextContentItemBackgroundColor(
  id: string,
  color: string | null | undefined
): UpdateTextContentItemBackgroundColorAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_TEXT_PAGE_CONTENT_ITEM_BACKGROUND_COLOR,
    payload: { id, color }
  };
}

export interface UpdateImageContentItemZoomAction {
  type: 'WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM';
  payload: {
    id: string;
    newZoom: number;
  };
}

export function updateImageContentItemZoom(id: string, newZoom: number): UpdateImageContentItemZoomAction {
  return {
    type: WYSIWYG_EDITOR_UPDATE_IMAGE_PAGE_CONTENT_ITEM_ZOOM,
    payload: { id, newZoom }
  };
}

export interface UndoAction {
  type: 'WYSIWYG_EDITOR_UNDO';
}

export function undo(): UndoAction {
  return {
    type: WYSIWYG_EDITOR_UNDO
  };
}

export interface RedoAction {
  type: 'WYSIWYG_EDITOR_REDO';
}

export function redo(): RedoAction {
  return {
    type: WYSIWYG_EDITOR_REDO
  };
}

export interface SaveSnapshotAction {
  type: 'WYSIWYG_EDITOR_SAVE_SNAPSHOT';
}

export function saveSnapshot(): SaveSnapshotAction {
  return {
    type: WYSIWYG_EDITOR_SAVE_SNAPSHOT
  };
}

export interface TextCacheAddItemAction {
  type: 'WYSIWYG_EDITOR_TEXT_CACHE_ADD_ITEM';
  payload: TTextBoxContentItem;
}

export function textCacheAddItem(newItem: TTextBoxContentItem): TextCacheAddItemAction {
  return {
    type: WYSIWYG_EDITOR_TEXT_CACHE_ADD_ITEM,
    payload: newItem
  };
}

export interface TextCacheRemoveItemAction {
  type: 'WYSIWYG_EDITOR_TEXT_CACHE_REMOVE_ITEM';
  payload: string;
}

export function textCacheRemoveItem(uuid: string): TextCacheRemoveItemAction {
  return {
    type: WYSIWYG_EDITOR_TEXT_CACHE_REMOVE_ITEM,
    payload: uuid
  };
}
